/**
 * A Vision line's Taxes / Charges — the "Tax details" form reverse deploy
 * adds under a Vision line (TRANSETUP.TAX_FORMS; TAXTRAN through a
 * parent_link). Shown by the ordinary child grid (`render`: the row pencil
 * opens the row's form), with Vision's calculation behind it:
 *
 * - opening it CALCULATES the line's taxes (`tax_calc`, Vision's own
 *   TaxCalculation), from the line as the page holds it — its current tax
 *   rows go along, so a Tax % changed before (chg_stat F) stays;
 * - Tax % can be changed on the rows Vision allows it (the tax code's
 *   EDIT_OPTION / SERIES_EDIT; the engine sends `_protect` on the others);
 *   Rate type and the rest are shown, never edited — Rate type always, even
 *   on a form imported before reverse deploy showed it; rows are never added
 *   or deleted;
 * - saving a row with a changed Tax % RECALCULATES the line's taxes
 *   (`tax_recalc`, Vision's ReCalculateTax: Tax amount by Rate type) and
 *   hands the rows and the line's new total (tax_amt) to `onApply`, which
 *   puts the rows under the line and the total on it. The save calculates
 *   from those rows again and stores TAXTRAN.
 *
 * When the taxes cannot be calculated (a runtime that does not run Vision's
 * business logic, or Vision's own refusal) it says why and shows the line's
 * stored rows, read-only.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import type { ColumnDef, EditMask, SqlModel } from '../types'
import { columnDbName, columnFormKey, isHidden } from '../utils'
import { rowForEvent } from '../screen/detailsBuffer'
import type { ChildStore } from '../screen/ChildFormTab'

type TaxRow = Record<string, unknown> & { _protect?: Record<string, 0 | 1> }

interface TaxAnswer {
  ok?: boolean
  error?: string
  rows?: TaxRow[]
  line?: Record<string, unknown>
}

/** Rate type, as Vision's Taxtran panel shows it. */
const RATE_TYPES: EditMask = {
  mask: 'ddlb',
  values: [
    { display: 'Fixed', data: 'F' }, { display: 'Percentage', data: 'P' },
    { display: 'Multiplier', data: 'M' },
  ],
}

const dbOf = (c: ColumnDef) => columnDbName(c).toLowerCase()

/** The form with Rate type shown next to Tax % even when the form hides it. */
function withRateType(sm: SqlModel): SqlModel {
  const wraps = sm.columns || []
  const at = wraps.findIndex(w => w?.column && dbOf(w.column) === 'rate_type')
  const rate = at >= 0 ? wraps[at].column : null
  if (!rate || (!isHidden(rate) && rate.edit_mask?.mask === 'ddlb')) return sm
  const perc = wraps.find(w => w?.column && dbOf(w.column) === 'tax_perc')?.column
  const shown: ColumnDef = {
    ...rate,
    hidden: '',
    protect: '1',
    heading: isHidden(rate) ? 'Rate type' : rate.heading,
    list_heading: isHidden(rate) ? 'Rate type' : rate.list_heading,
    edit_mask: rate.edit_mask?.mask === 'ddlb' ? rate.edit_mask : RATE_TYPES,
    ...(perc ? { x: perc.x, y: perc.y, group_name: perc.group_name } : {}),
  }
  const rest = wraps.filter((_, i) => i !== at)
  const pi = rest.findIndex(w => w?.column && dbOf(w.column) === 'tax_perc')
  rest.splice(pi < 0 ? rest.length : pi + 1, 0, { ...wraps[at], column: shown })
  return { ...sm, columns: rest }
}

const NUMERIC = /^(NUMBER|NUMERIC|DECIMAL|INTEGER|BIGINT|SMALLINT|DOUBLE|FLOAT|REAL)$/i

/** Grid widths that fit the values. The imported widths are Vision's own
 *  (~50px), which cut an amount down to "9…"; never narrows a column. */
function roomyColumns(sm: SqlModel): SqlModel {
  return {
    ...sm,
    columns: (sm.columns || []).map(w => {
      const c = w?.column
      if (!c || isHidden(c)) return w
      const floor = c.edit_mask?.mask === 'ddlb' ? 104 : NUMERIC.test(String(c.col_type || '')) ? 96 : 64
      const width = Number(c.width)
      return Number.isFinite(width) && width >= floor ? w : { ...w, column: { ...c, width: floor } }
    }),
  }
}

/** The "Tax details" form reverse deploy adds under a Vision line. */
export function isVisionTaxForm(sm: SqlModel | null | undefined): boolean {
  const s = (sm || {}) as unknown as Record<string, unknown>
  return !!s.twasta_only && String(sm?.main_table || '').toUpperCase() === 'TAXTRAN' && !!s.parent_link
}

interface Props {
  /** The Tax details form. */
  taxSm: SqlModel
  /** Runs one engine action ON THE TAX DETAILS FORM (`form_no` is added). */
  run: (action: 'tax_calc' | 'tax_recalc', payload: Record<string, unknown>) => Promise<unknown>
  /** The line as the page holds it. */
  line: Record<string, unknown>
  /** The line's header (the record being edited). */
  headerValues?: Record<string, unknown>
  /** The line's tax rows the page already holds; undefined when it has none
   *  (a saved line's are then read from TAXTRAN by the server). */
  heldRows?: Array<Record<string, unknown>>
  /** The record is being edited (not added). */
  editMode: boolean
  /** Record id, when the record is saved. */
  recordTxnId?: string
  /** Nothing may be changed (a view-only record). */
  readOnly?: boolean
  /** After a Tax % change: the recalculated rows (to keep under the line) and
   *  the line columns Vision changed (tax_amt …). */
  onApply: (rows: Array<Record<string, unknown>>, lineChanges: Record<string, unknown>) => void
  onError?: (message: string) => void
  /** Called when the taxes cannot be calculated, so the host loads the stored
   *  rows into `heldRows` if it has not. */
  onNeedRows?: () => void
  /** The ordinary child grid over the rows (fixed rows: no Add / Delete). */
  render: (sm: SqlModel, store: ChildStore, readOnly: boolean) => ReactNode
}

export default function VisionTaxPanel({
  taxSm, run, line, headerValues, heldRows, editMode, recordTxnId, readOnly = false,
  onApply, onError, onNeedRows, render,
}: Props) {
  const [rows, setRows] = useState<TaxRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(true)
  const sm = useMemo(() => roomyColumns(withRateType(taxSm)), [taxSm])
  const percCol = (sm.columns || []).find(w => w?.column && dbOf(w.column) === 'tax_perc')?.column
  const percKey = percCol ? columnFormKey(percCol) : 'tax_perc'

  const base = useRef({ line, headerValues, editMode, recordTxnId })
  base.current = { line, headerValues, editMode, recordTxnId }

  const ask = async (
    action: 'tax_calc' | 'tax_recalc', extra: Record<string, unknown>,
  ): Promise<TaxAnswer> => {
    const b = base.current
    try {
      const res = await run(action, {
        line: rowForEvent(b.line),
        header_values: b.headerValues || {},
        edit_mode: b.editMode ? 'edit' : 'add',
        ...(b.recordTxnId ? { txn_id: b.recordTxnId } : {}),
        ...extra,
      }) as TaxAnswer
      if (res?.ok === false || res?.error) return { error: res?.error || 'The taxes could not be calculated' }
      return res || {}
    } catch (err) {
      const e = err as { response?: { data?: { detail?: unknown } }; message?: string }
      const detail = e?.response?.data?.detail
      return { error: typeof detail === 'string' ? detail : (e?.message || 'The taxes could not be calculated') }
    }
  }

  // Calculated once per opening, from the line as it was then.
  useEffect(() => {
    let cancelled = false
    const taxes = heldRows?.filter(r => !r._deleted).map(rowForEvent)
    void ask('tax_calc', taxes ? { taxes } : {}).then(res => {
      if (cancelled) return
      setBusy(false)
      if (res.error) { setError(res.error); onNeedRows?.(); return }
      setRows(res.rows || [])
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const recalc = async (index: number, value: number) => {
    if (!rows) return
    const before = rows
    const next = rows.map((r, i) => (i === index ? { ...r, [percKey]: value } : r))
    setRows(next)
    setBusy(true)
    const res = await ask('tax_recalc', { taxes: next.map(rowForEvent), edited_row: index })
    setBusy(false)
    if (res.error || !res.rows) {
      setRows(before)
      onError?.(res.error || 'The taxes could not be recalculated')
      return
    }
    setRows(res.rows)
    onApply(res.rows.map(r => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { _protect, ...rest } = r
      return rest
    }), res.line || {})
  }

  // Calculated rows (Tax % open where Vision allows it); failing that, the
  // stored ones, read-only.
  const shown: TaxRow[] = rows
    ? rows.map(r => ({ ...r, _protect: { [percKey]: r._protect?.[percKey] === 1 ? 1 : 0 } }))
    : error ? (heldRows || []).filter(r => !r._deleted) as TaxRow[] : []

  const store: ChildStore = {
    rows: shown,
    add: () => {},
    remove: () => {},
    ensureLoaded: () => {},
    edit: (index, patch) => {
      if (!rows || !(percKey in patch)) return
      const raw = patch[percKey]
      const value = raw === null || raw === undefined || String(raw).trim() === '' ? 0 : Number(raw)
      if (!Number.isFinite(value) || Number(rows[index]?.[percKey] ?? 0) === value) return
      void recalc(index, value)
    },
  }

  return (
    <div data-tw-vision-tax-panel="" style={{ display: 'flex', flexDirection: 'column' }}>
      {busy && (
        <div style={{
          padding: '4px 8px', fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <Loader2 size={12} className="spin" aria-hidden /> Calculating taxes…
        </div>
      )}
      {error && (
        <div role="alert" style={{ padding: '6px 8px', fontSize: '0.8125rem', color: '#b42318' }}>
          {error}
        </div>
      )}
      {(rows || error) && render(sm, store, readOnly || !rows)}
    </div>
  )
}
